import { Stack } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';

import { FeatureFlag } from '@revelio/core';
import { Radio, RadioGroup, Text } from '@revelio/ui';

import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { savePreferencesForScope } from '../api/user-preferences-api';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { usePreferenceToast } from '../hooks/usePreferenceToast';
import {
  type ColorModePreference,
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.ColorMode,
  label: 'Appearance',
};

type PreferencesByScope = Record<PreferenceScope, Partial<UserPreferences>>;

const OPTIONS: {
  value: ColorModePreference;
  label: string;
  description: string;
}[] = [
  {
    value: 'system',
    label: 'System default',
    description: 'Match your operating system’s light or dark setting.',
  },
  {
    value: 'light',
    label: 'Light',
    description: 'Always use the light theme.',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Always use the dark theme.',
  },
  {
    value: 'legacy',
    label: 'Legacy',
    description: 'Use the original pre-redesign styling.',
  },
];

export default function ColorModeEditor({
  preferences,
  diff,
}: PreferenceEditorProps) {
  const newDashboardLayoutEnabled = useUnleashFlag(
    FeatureFlag.DashboardNewLayout
  );
  const queryClient = useQueryClient();
  const showToast = usePreferenceToast();

  // Derive directly from props — `preferences` is the single source of truth.
  const mode = preferences[UserPreferenceKey.ColorMode] ?? 'legacy';

  const { mutate: persistColorMode } = useMutation({
    mutationFn: (value: ColorModePreference) =>
      savePreferencesForScope(PreferenceScope.GLOBAL, {
        [UserPreferenceKey.ColorMode]: value,
      }),
    onMutate: async (value: ColorModePreference) => {
      const previousStored = PreferenceBrowserStorage.get(
        PreferenceScope.GLOBAL
      )[UserPreferenceKey.ColorMode];
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.ColorMode,
        value
      );
      await queryClient.cancelQueries({ queryKey: ['user-preferences'] });
      const previousByScope = queryClient.getQueryData<PreferencesByScope>([
        'user-preferences',
      ]);
      queryClient.setQueryData<PreferencesByScope>(
        ['user-preferences'],
        (prev) =>
          prev
            ? {
                ...prev,
                [PreferenceScope.GLOBAL]: {
                  ...prev[PreferenceScope.GLOBAL],
                  [UserPreferenceKey.ColorMode]: value,
                },
              }
            : prev
      );
      return { previousStored, previousByScope };
    },
    onError: (error, _value, context) => {
      if (context?.previousByScope) {
        queryClient.setQueryData(['user-preferences'], context.previousByScope);
      }
      if (context?.previousStored === undefined) {
        PreferenceBrowserStorage.remove(
          PreferenceScope.GLOBAL,
          UserPreferenceKey.ColorMode
        );
      } else {
        PreferenceBrowserStorage.update(
          PreferenceScope.GLOBAL,
          UserPreferenceKey.ColorMode,
          context.previousStored
        );
      }
      showToast('Failed to save appearance', 'error', { error });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  // Hide the control entirely when the flag is off.
  if (!newDashboardLayoutEnabled) {
    return null;
  }

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Appearance"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="Choose between light, dark, or matching your system setting."
      />
      <RadioGroup
        value={mode}
        onChange={(val) => persistColorMode(val as ColorModePreference)}
        className={styles.radioGroup}
      >
        <Stack spacing={3}>
          {OPTIONS.map((option) => (
            <Stack key={option.value} spacing={1}>
              <Radio value={option.value} className={styles.appearanceRadio}>
                {option.label}
              </Radio>
              <Text
                fontSize="x-sm"
                className={classNames(
                  styles.description,
                  styles.descriptionIndent
                )}
              >
                {option.description}
              </Text>
            </Stack>
          ))}
        </Stack>
      </RadioGroup>
    </>
  );
}
